import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Backdrop,
    Button,
    Grid,
    Card,
    CardContent,
    Alert,
    Divider,
    TextField,
    InputAdornment,
    IconButton,
} from "@mui/material";
import { ArrowBack, ContentCopy, Visibility, VisibilityOff } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import base_url from "../../constants";
import useNotification from "../common/Notification";
import { WB_SX } from "../../ui/ux/uiTokens";

const TemplateCurl = () => {
    const { templateid } = useParams();
    const navigate = useNavigate();
    const notify = useNotification();

    const [loading, setLoading] = useState(true);
    const [curlData, setCurlData] = useState(null);
    const [error, setError] = useState("");
    const [apiKeyState, setApiKeyState] = useState({
        status: "loading",
        apiKey: "",
        message: "",
    });
    const [showApiKey, setShowApiKey] = useState(false);

    useEffect(() => {
        const fetchPage = async () => {
            if (!templateid) {
                setError("Template ID is required");
                setLoading(false);
                return;
            }

            setLoading(true);
            setError("");
            setShowApiKey(false);
            setApiKeyState({ status: "loading", apiKey: "", message: "" });

            const [curlResult, apiKeyResult] = await Promise.allSettled([
                axiosInstance.post(
                    `${base_url}/whatsapp-business/v1/generate-curl`,
                    { template_id: String(templateid) }
                ),
                axiosInstance.get(`${base_url}/user/api-key`),
            ]);

            if (curlResult.status === "fulfilled") {
                setCurlData(curlResult.value.data?.data || curlResult.value.data);
            } else {
                const err = curlResult.reason;
                const message =
                    err.response?.data?.error ||
                    err.response?.data?.message ||
                    "Failed to generate the cURL.";
                setCurlData(null);
                setError(typeof message === "string" ? message : "Failed to generate the cURL.");
            }

            if (apiKeyResult.status === "fulfilled") {
                const data = apiKeyResult.value.data?.data || apiKeyResult.value.data || {};
                if (data.has_api_key && data.api_key) {
                    setApiKeyState({ status: "ready", apiKey: data.api_key, message: "" });
                } else {
                    setApiKeyState({
                        status: "missing",
                        apiKey: "",
                        message: "No API key has been generated for this account yet.",
                    });
                }
            } else {
                const err = apiKeyResult.reason;
                if (err.response?.status === 403) {
                    setApiKeyState({
                        status: "forbidden",
                        apiKey: "",
                        message: "Only a WhatsApp, Email, or CRM admin can view this API key.",
                    });
                } else {
                    const message =
                        err.response?.data?.error ||
                        err.response?.data?.message ||
                        "Could not load the API key.";
                    setApiKeyState({
                        status: "error",
                        apiKey: "",
                        message: typeof message === "string" ? message : "Could not load the API key.",
                    });
                }
            }

            setLoading(false);
        };

        fetchPage();
    }, [templateid]);

    const copyText = async (value, label) => {
        if (!value) return;
        try {
            await navigator.clipboard.writeText(value);
            notify(`${label} copied`, "success");
        } catch (err) {
            notify(`Could not copy the ${label.toLowerCase()}`, "error");
        }
    };

    const curlCommand = applyApiKey(curlData?.curl_command, apiKeyState.apiKey);
    const visibleCurlCommand = showApiKey ? curlCommand : maskApiKeyInCommand(curlCommand, apiKeyState.apiKey);
    const steps = buildSteps(curlData);

    if (loading) {
        return (
            <Backdrop
                sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
                open
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        );
    }

    return (
        <Box sx={WB_SX.page}>
            <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBack />}
                    onClick={() => navigate(`/whatsapp-business/template/${templateid}`)}
                    sx={WB_SX.secondaryButton}
                >
                    Back
                </Button>
                <Typography variant="h5" sx={{ ...WB_SX.pageTitle, mb: 0 }}>
                    API cURL{" "}
                    <Box component="span" sx={{ fontWeight: 500, color: "text.secondary", fontSize: "0.72em" }}>
                        (for developer)
                    </Box>
                </Typography>
            </Box>

            {error && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                    {curlData && (
                        <Card elevation={0} sx={{ ...WB_SX.card, mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2, ...WB_SX.sectionTitle }}>
                                    Template
                                </Typography>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                    <InfoRow label="Name" value={curlData.template_name} />
                                    <InfoRow label="Template ID" value={curlData.template_id} mono />
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    <Card elevation={0} sx={{ ...WB_SX.card, mb: curlData ? 3 : 0 }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 1, ...WB_SX.sectionTitle }}>
                                API key
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                This key is sent as the X-API-Key header. It stays hidden until you choose to show it.
                            </Typography>
                            {apiKeyState.status === "ready" ? (
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={showApiKey ? apiKeyState.apiKey : maskApiKey(apiKeyState.apiKey)}
                                    InputProps={{
                                        readOnly: true,
                                        sx: { fontFamily: "monospace" },
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    aria-label={showApiKey ? "Hide API key" : "Show API key"}
                                                    onClick={() => setShowApiKey((visible) => !visible)}
                                                >
                                                    {showApiKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    aria-label="Copy API key"
                                                    onClick={() => copyText(apiKeyState.apiKey, "API key")}
                                                >
                                                    <ContentCopy fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            ) : (
                                <Alert severity={apiKeyState.status === "missing" ? "info" : "warning"}>
                                    {apiKeyState.message}
                                </Alert>
                            )}
                        </CardContent>
                    </Card>

                    {curlData && (
                        <Card elevation={0} sx={WB_SX.card}>
                            <CardContent>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
                                    <Typography variant="h6" sx={WB_SX.sectionTitle}>
                                        cURL command
                                    </Typography>
                                    <Button
                                        size="small"
                                        startIcon={<ContentCopy />}
                                        onClick={() => copyText(curlCommand, "cURL")}
                                        sx={WB_SX.secondaryButton}
                                    >
                                        Copy
                                    </Button>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    Copy this command, then fill in the blanks before you send it. The key is hidden here. Copy still includes the real key.
                                </Typography>
                                <CodeBlock text={visibleCurlCommand} />
                            </CardContent>
                        </Card>
                    )}
                </Grid>

                <Grid item xs={12} md={5}>
                    {steps.length > 0 && (
                        <Card elevation={0} sx={{ ...WB_SX.card, mb: 3 }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 1.5, ...WB_SX.sectionTitle }}>
                                    How to use this
                                </Typography>
                                {steps.map((item, index) => (
                                    <Typography key={index} variant="body2" sx={{ mb: 1.25 }}>
                                        {index + 1}. {item}
                                    </Typography>
                                ))}
                            </CardContent>
                        </Card>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

const maskApiKey = (apiKey) => {
    if (!apiKey) return "";
    if (apiKey.length <= 4) return "•".repeat(8);
    return `••••••••${apiKey.slice(-4)}`;
};

const applyApiKey = (command, apiKey) => {
    if (!command || !apiKey) return command;
    return command.replace(/X-API-Key:\s*[^'"\\\s]+/g, `X-API-Key: ${apiKey}`);
};

const maskApiKeyInCommand = (command, apiKey) => {
    if (!command) return command;
    const masked = maskApiKey(apiKey);
    let next = command;
    if (apiKey) {
        next = next.split(apiKey).join(masked);
    }
    return next.replace(/X-API-Key:\s*[^'"\\\s]+/g, `X-API-Key: ${masked || "••••••••"}`);
};

const buildSteps = (data) => {
    if (!data) return [];
    const keys = Object.keys(data.placeholder_variables || {}).join(" ").toLowerCase();
    const header = String(data.header_format || "").toUpperCase();
    const steps = [
        "Copy the command.",
        "Look for words inside double curly brackets, like {{name}}. Replace each one with the real value, and delete the brackets.",
        "Put the phone number with the country code, such as +919876543210.",
    ];
    if (keys.includes("body_param") || keys.includes("header_param") || keys.includes("footer_param") || data.has_parameters) {
        steps.push("Fill the message blanks in the same order they appear in the template. The first blank is param1, the next is param2.");
    }
    if (keys.includes("media") || ["IMAGE", "VIDEO", "DOCUMENT"].includes(header)) {
        const kind = header === "VIDEO" ? "video" : header === "DOCUMENT" ? "file" : "picture";
        steps.push(`For the ${kind}, paste a full web link. A short name or an id will not work.`);
    }
    if (/otp|authentication/i.test(String(data.note || ""))) {
        steps.push("If this message is a login code, use the same code in the message and on the button.");
    }
    steps.push("You can choose a campaign name so this send is easy to find later.");
    steps.push("Keep this command private. Anyone who has it can send messages from your account.");
    steps.push("WhatsApp will send this only if the template is already approved.");
    return steps;
};

const InfoRow = ({ label, value, mono }) => (
    <Box>
        <Typography variant="subtitle2" color="text.secondary">
            {label}
        </Typography>
        <Typography
            variant="body2"
            sx={{ fontWeight: 600, fontFamily: mono ? "monospace" : "inherit", wordBreak: "break-all" }}
        >
            {value || "N/A"}
        </Typography>
        <Divider sx={{ mt: 1 }} />
    </Box>
);

const CodeBlock = ({ text }) => (
    <Paper
        variant="outlined"
        sx={{
            p: 2,
            backgroundColor: "#111827",
            color: "#e5e7eb",
            overflow: "auto",
            maxHeight: 420,
        }}
    >
        <Typography
            component="pre"
            sx={{
                m: 0,
                fontFamily: "monospace",
                fontSize: 12,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
            }}
        >
            {text}
        </Typography>
    </Paper>
);

export default TemplateCurl;
